import { Download, File as FileIcon } from 'lucide-react';
import {
    type ImgHTMLAttributes,
    memo,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { type Components } from 'react-markdown';

import { type MessageUploadAttachment } from '@apps/web/components/phoebe_chat/transcript_types';
import { Markdown } from '@apps/web/components/ui/markdown';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    formatAttachmentSize,
    isImageAttachment,
    isMarkdownAttachment,
    type V3ChatAttachment,
} from '@apps/web/features/phoebe_chat_v3/model/attachment';
import { type V3PresentationContext } from '@apps/web/features/phoebe_chat_v3/model/presentation_context';
import { DownloadButton } from '@apps/web/features/phoebe_chat_v3/ui/chat_attachment_download_button';
import {
    fetchAttachmentBlob,
    fetchAttachmentMarkdown,
    useAttachmentPreviewNearViewport,
    useAttachmentSWR,
} from '@apps/web/features/phoebe_chat_v3/ui/chat_attachment_fetch';
import { useMode } from '@apps/web/lib/mode_store';

const MAX_INLINE_IMAGE_BYTES = 2 * 1024 * 1024;
const MAX_PREVIEW_IMAGE_DIMENSION = 8192;
const MAX_PREVIEW_IMAGE_PIXELS = 25_000_000;

function ChatAttachmentFileCard({
    attachment,
    context,
}: {
    readonly attachment: V3ChatAttachment;
    readonly context: V3PresentationContext;
}) {
    return (
        <div
            data-testid="v3-chat-attachment-file-card"
            className="flex max-w-[520px] items-center gap-3 rounded-2xl border border-[#DBE3E3] bg-[#F0F5F5] px-4 py-3"
        >
            <span
                aria-hidden="true"
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white text-[#617578] [&>svg]:size-4"
            >
                <FileIcon />
            </span>
            <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-[#002429]">
                    {attachment.filename}
                </div>
                <div className="text-xs text-[#617578]">
                    {formatAttachmentSize(attachment.sizeBytes)}
                </div>
            </div>
            <DownloadButton
                context={context}
                runId={attachment.runId}
                attachmentId={attachment.attachmentId}
                filename={attachment.filename}
            >
                <Download aria-hidden="true" className="size-4" />
                <span>Download</span>
            </DownloadButton>
        </div>
    );
}

function ChatAttachmentImageCard({
    attachment,
    context,
}: {
    readonly attachment: V3ChatAttachment;
    readonly context: V3PresentationContext;
}) {
    // Fetch the image bytes through the api client (X-Mode + cookies) and
    // hand the <img> a blob URL. A cross-origin `<img src>` request would
    // otherwise be missing the mode header and the endpoint would fail
    // closed with 422.
    const { organizationId, mode } = context;
    const { ref, isNearViewport } = useAttachmentPreviewNearViewport();
    const fetcher = useCallback(
        (signal: AbortSignal) =>
            fetchAttachmentBlob({
                organizationId,
                runId: attachment.runId,
                attachmentId: attachment.attachmentId,
                mode,
                download: false,
                signal,
            }),
        [attachment.attachmentId, attachment.runId, mode, organizationId],
    );
    const { data: blob, error } = useAttachmentSWR<Blob>(
        isNearViewport
            ? [
                  'chat-attachment-image',
                  organizationId,
                  attachment.runId,
                  attachment.attachmentId,
                  mode,
              ]
            : null,
        fetcher,
    );
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [imageTooLarge, setImageTooLarge] = useState(false);
    useEffect(() => {
        if (!blob) {
            setBlobUrl(null);
            return;
        }
        const nextBlobUrl = URL.createObjectURL(blob);
        setBlobUrl(nextBlobUrl);
        return () => {
            URL.revokeObjectURL(nextBlobUrl);
        };
    }, [blob]);
    if (error || imageTooLarge) {
        return (
            <ChatAttachmentFileCard attachment={attachment} context={context} />
        );
    }
    return (
        <div
            data-testid="v3-chat-attachment-image-card"
            ref={ref}
            className="flex max-w-[520px] flex-col gap-2"
        >
            {blobUrl ? (
                <img
                    src={blobUrl}
                    alt={attachment.filename}
                    referrerPolicy="no-referrer"
                    onLoad={(event) => {
                        const { naturalHeight, naturalWidth } =
                            event.currentTarget;
                        setImageTooLarge(
                            naturalWidth > MAX_PREVIEW_IMAGE_DIMENSION ||
                                naturalHeight > MAX_PREVIEW_IMAGE_DIMENSION ||
                                naturalWidth * naturalHeight >
                                    MAX_PREVIEW_IMAGE_PIXELS,
                        );
                    }}
                    className="max-h-[420px] w-auto max-w-full rounded-2xl border border-[#DBE3E3] object-contain"
                />
            ) : (
                <div className="flex h-32 items-center justify-center rounded-2xl border border-[#DBE3E3] bg-[#F0F5F5] text-xs text-[#617578]">
                    Loading {attachment.filename}…
                </div>
            )}
            <div className="flex items-center justify-between gap-3 text-xs text-[#617578]">
                <span className="truncate">
                    {attachment.filename} &middot;{' '}
                    {formatAttachmentSize(attachment.sizeBytes)}
                </span>
                <DownloadButton
                    context={context}
                    runId={attachment.runId}
                    attachmentId={attachment.attachmentId}
                    filename={attachment.filename}
                    className="shrink-0"
                >
                    <Download aria-hidden="true" className="size-3.5" />
                    <span>Download</span>
                </DownloadButton>
            </div>
        </div>
    );
}

// Hardened <img> renderer for agent-attached markdown: only inline data URIs
// (embedded PNG/JPEG etc.) render. Every remote URL turns into a plain-text
// placeholder so opening the file cannot leak the viewer's IP to an arbitrary
// origin (react-markdown does not sanitize the img src attribute on its own).
const ATTACHMENT_MARKDOWN_COMPONENTS: Components = {
    img: ({ src, alt }: ImgHTMLAttributes<HTMLImageElement>) => {
        if (typeof src === 'string' && src.startsWith('data:')) {
            return (
                <img
                    src={src}
                    alt={alt}
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    className="my-2 h-auto max-h-96 max-w-[80%] rounded-md border"
                />
            );
        }
        return (
            <span className="text-[#617578] italic">
                [image omitted: {alt || 'remote image blocked'}]
            </span>
        );
    },
};

function ChatAttachmentMarkdownCard({
    attachment,
    context,
}: {
    readonly attachment: V3ChatAttachment;
    readonly context: V3PresentationContext;
}) {
    const { organizationId, mode } = context;
    const { ref, isNearViewport } = useAttachmentPreviewNearViewport();
    const fetcher = useCallback(
        (signal: AbortSignal) =>
            fetchAttachmentMarkdown({
                organizationId,
                runId: attachment.runId,
                attachmentId: attachment.attachmentId,
                mode,
                sizeBytes: attachment.sizeBytes,
                signal,
            }),
        [
            attachment.attachmentId,
            attachment.runId,
            attachment.sizeBytes,
            mode,
            organizationId,
        ],
    );
    const { data, error, isLoading } = useAttachmentSWR<string>(
        isNearViewport
            ? [
                  'chat-attachment-markdown',
                  organizationId,
                  attachment.runId,
                  attachment.attachmentId,
                  mode,
              ]
            : null,
        fetcher,
    );
    if (error) {
        return (
            <ChatAttachmentFileCard attachment={attachment} context={context} />
        );
    }
    return (
        <div
            data-testid="v3-chat-attachment-markdown-card"
            ref={ref}
            className="flex max-w-[620px] flex-col gap-2 rounded-2xl border border-[#DBE3E3] bg-white px-4 py-3"
        >
            {isLoading || data === undefined ? (
                <div className="flex items-center gap-2 text-xs text-[#617578]">
                    <Spinner size="sm" />
                    <span>Loading {attachment.filename}…</span>
                </div>
            ) : (
                <Markdown
                    components={ATTACHMENT_MARKDOWN_COMPONENTS}
                    skipHtml={true}
                >
                    {data}
                </Markdown>
            )}
            <div className="flex items-center justify-between gap-3 border-t border-[#DBE3E3] pt-2 text-xs text-[#617578]">
                <span className="truncate">
                    {attachment.filename} &middot;{' '}
                    {formatAttachmentSize(attachment.sizeBytes)}
                </span>
                <DownloadButton
                    context={context}
                    runId={attachment.runId}
                    attachmentId={attachment.attachmentId}
                    filename={attachment.filename}
                    className="shrink-0"
                >
                    <Download aria-hidden="true" className="size-3.5" />
                    <span>Download</span>
                </DownloadButton>
            </div>
        </div>
    );
}

export const V3ChatAttachmentCard = memo(function V3ChatAttachmentCard({
    attachment,
    context,
}: {
    readonly attachment: V3ChatAttachment;
    readonly context: V3PresentationContext;
}) {
    const effectiveContext = { ...context, mode: useMode() ?? context.mode };
    if (
        isImageAttachment(attachment) &&
        attachment.sizeBytes <= MAX_INLINE_IMAGE_BYTES
    ) {
        return (
            <ChatAttachmentImageCard
                attachment={attachment}
                context={effectiveContext}
            />
        );
    }
    if (isMarkdownAttachment(attachment)) {
        return (
            <ChatAttachmentMarkdownCard
                attachment={attachment}
                context={effectiveContext}
            />
        );
    }
    return (
        <ChatAttachmentFileCard
            attachment={attachment}
            context={effectiveContext}
        />
    );
});

export const V3UserUploadAttachmentCard = memo(
    function V3UserUploadAttachmentCard({
        upload,
        context,
    }: {
        readonly upload: MessageUploadAttachment;
        readonly context: V3PresentationContext;
    }) {
        return (
            <V3ChatAttachmentCard
                attachment={{
                    kind: 'chat-attachment',
                    version: 1,
                    runId: upload.runId,
                    attachmentId: upload.attachmentId,
                    filepath: upload.filename,
                    filename: upload.filename,
                    contentType: upload.contentType,
                    disposition: upload.disposition,
                    sizeBytes: upload.sizeBytes,
                }}
                context={context}
            />
        );
    },
);
